import { useCallback, useSyncExternalStore } from 'react';
import { useRecoilStoreID } from 'recoil';
import type { StoreID } from 'recoil';
import type { ViewportTypeKeys } from 'config/constants';

export type DisplayConfigurationKey = {
  stackSmid: string;
  viewType: ViewportTypeKeys;
  viewportId: string;
};

const hydratedKeysByStore = new Map<StoreID, Set<string>>();
const subscribers = new Set<() => void>();

export function displayConfigurationHydrationKey({
  stackSmid,
  viewType,
  viewportId,
}: DisplayConfigurationKey): string {
  return JSON.stringify([stackSmid, viewType, viewportId]);
}

export function markDisplayConfigurationHydrated(storeID: StoreID, key: string): void {
  let hydratedKeys = hydratedKeysByStore.get(storeID);
  if (hydratedKeys == null) {
    hydratedKeys = new Set();
    hydratedKeysByStore.set(storeID, hydratedKeys);
  }
  hydratedKeys.add(key);
  for (const notify of subscribers) notify();
}

export function clearDisplayConfigurationHydrated(storeID: StoreID, key: string): void {
  const hydratedKeys = hydratedKeysByStore.get(storeID);
  hydratedKeys?.delete(key);
  if (hydratedKeys?.size === 0) hydratedKeysByStore.delete(storeID);
  for (const notify of subscribers) notify();
}

function subscribe(onStoreChange: () => void): () => void {
  subscribers.add(onStoreChange);
  return () => subscribers.delete(onStoreChange);
}

export function useAreDisplayConfigurationsHydrated(keys: readonly string[]): boolean {
  const storeID = useRecoilStoreID();
  const getSnapshot = useCallback(
    () => keys.every((key) => hydratedKeysByStore.get(storeID)?.has(key) === true),
    [keys, storeID]
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}
